/**
 * PRIMEX — assets/primex-gallery-tabs.js (v1.0)
 *
 * Companion to snippets/primex-gallery-tabs.liquid.
 * Three jobs, all ADDITIVE — this file never writes into the pricing
 * configurator (primex-configurator.js v14.x stays byte-identical):
 *
 *   1. Tab switching (Produktbilder / Konfiguration)
 *   2. Gallery: Swiper if the theme's Swiper is loaded (thumbs-synced),
 *      otherwise a CSS scroll-snap fallback. Clicking an image slide opens
 *      the existing product-media-modal.
 *   3. Live preview: READS the configurator's inputs (#px-width, #px-height,
 *      input[name="px-fcolor"], .px-upinput, catalogue .px-dtile radios) and
 *      mirrors them onto the preview frame. A MutationObserver on
 *      #px-price-net re-syncs after every compute() — that covers stepper
 *      buttons and every programmatic change without touching the engine.
 */
(function () {
  'use strict';

  var wrap = document.querySelector('[data-pxg]');
  if (!wrap) return;

  var sectionId = wrap.dataset.sectionId;

  /* ============================ 1. TABS ============================ */
  var tabs = wrap.querySelectorAll('[data-pxg-tab]');
  var panels = wrap.querySelectorAll('[data-pxg-panel]');
  var pxRoot = document.getElementById('px-configurator');

  function activateTab(name) {
    tabs.forEach(function (t) {
      var on = t.dataset.pxgTab === name;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', String(on));
    });
    panels.forEach(function (p) {
      var on = p.dataset.pxgPanel === name;
      p.classList.toggle('is-active', on);
      if (on) p.removeAttribute('hidden'); else p.setAttribute('hidden', '');
    });
    if (name === 'config') renderPreview();
  }
  tabs.forEach(function (t) {
    t.addEventListener('click', function () { activateTab(t.dataset.pxgTab); });
  });

  // The live preview literally reads #px-width / #px-height and draws a flat
  // rectangle — that's only meaningful for the subset of products that (a)
  // let the customer type free dimensions at all, and (b) are 2D. Per the
  // 7-category pricing reference:
  //   Category 1 (Formula, 2D)  -> #px-width + #px-height, no #px-depth  -> YES
  //   Category 1 (Formula, 3D, e.g. Messewürfel) -> #px-depth also exists -> NO
  //   Category 2 (Round)        -> no free dims at all, a variant dropdown -> NO
  //   Category 3 (Architect)    -> same 2D formula system as Category 1   -> YES
  //   Category 4/5 (Handelsware)-> no pf, no dims, native variant price   -> NO
  //   Category 6 (Messesystem textile add-on, layered on 4/5) -> no dims  -> NO
  //   Category 7 (Stoffbanner)  -> #px-width + #px-height, no depth       -> YES
  var hasFlatFreeDims = false;
  if (pxRoot) {
    var pxW = document.getElementById('px-width');
    var pxH = document.getElementById('px-height');
    var pxT = document.getElementById('px-depth');
    hasFlatFreeDims = !!(pxW && pxH && !pxT);
  }

  // No configurator on this page, or this product's configuration doesn't
  // fit the live preview (round / Handelsware / textile add-on / 3D formula)
  // -> there's only one tab left (Produktbilder), which is pointless on its
  // own, so hide the WHOLE tab bar and just show the gallery.
  if (!pxRoot || !hasFlatFreeDims) {
    var tabBar = wrap.querySelector('.pxg-tabs');
    if (tabBar) tabBar.style.display = 'none';
    // make sure the photos panel is the one showing (never the config panel)
    activateTab('photos');
  }

  /* ============================ 2. GALLERY ============================ */
  var mainEl = document.getElementById('pxgMain-' + sectionId);
  var thumbsEl = document.getElementById('pxgThumbs-' + sectionId);
  var mainSwiper = null;

  function initSwiper() {
    if (typeof window.Swiper !== 'function' || !mainEl) return false;
    try {
      var thumbsSwiper = null;
      if (thumbsEl) {
        thumbsSwiper = new window.Swiper(thumbsEl, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: true,
          watchSlidesProgress: true
        });
      }
      var opts = {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 500,
        keyboard: { enabled: true, onlyInViewport: true },
        navigation: {
          nextEl: '#pxgMain-' + sectionId + ' .pxg-next',
          prevEl: '#pxgMain-' + sectionId + ' .pxg-prev'
        }
      };
      if (thumbsSwiper) opts.thumbs = { swiper: thumbsSwiper };
      mainSwiper = new window.Swiper(mainEl, opts);
      return true;
    } catch (e) {
      console.warn('PXG: Swiper init failed, using fallback', e);
      return false;
    }
  }

  function initFallback() {
    if (!mainEl) return;
    mainEl.classList.add('pxg-fallback');
    if (thumbsEl) thumbsEl.classList.add('pxg-fallback');
    var track = mainEl.querySelector('.swiper-wrapper');
    var prev = mainEl.querySelector('.pxg-prev');
    var next = mainEl.querySelector('.pxg-next');
    function slideBy(dir) {
      if (track) track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { slideBy(-1); });
    if (next) next.addEventListener('click', function () { slideBy(1); });
    if (thumbsEl) {
      thumbsEl.querySelectorAll('.pxg-thumb').forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          thumbsEl.querySelectorAll('.pxg-thumb').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
          if (track) track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        });
      });
    }
  }

  if (!initSwiper()) initFallback();
  else if (thumbsEl) {
    // Swiper's thumbs module handles sync; add is-active for our CSS too.
    thumbsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.pxg-thumb');
      if (!btn) return;
      thumbsEl.querySelectorAll('.pxg-thumb').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
    });
  }

  // Clicking an image slide opens the existing product-media-modal.
  var modal = document.getElementById('ProductModal-' + sectionId);
  if (mainEl && modal) {
    mainEl.addEventListener('click', function (e) {
      var slide = e.target.closest('.pxg-slide');
      if (!slide || slide.dataset.mediaType !== 'image') return;
      try {
        if (typeof modal.show === 'function') {
          modal.show(slide);
        } else {
          modal.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }
      } catch (err) {
        console.warn('PXG: could not open media modal', err);
        return;
      }
      // best effort: jump the modal's swiper to the clicked media
      try {
        var msEl = modal.querySelector('.swiper-product-modal');
        var ms = msEl && msEl.swiper;
        if (ms) {
          var slides = msEl.querySelectorAll('.swiper-slide');
          for (var i = 0; i < slides.length; i++) {
            if (slides[i].dataset.mediaId === slide.dataset.mediaId) {
              ms.slideTo(i, 0);
              break;
            }
          }
        }
      } catch (err2) { /* non-fatal */ }
    });
  }

  /* ============================ 3. LIVE PREVIEW ============================ */
  var frame = wrap.querySelector('[data-pxg-frame]');
  var frameImg = wrap.querySelector('[data-pxg-frame-img]');
  var frameLabel = wrap.querySelector('[data-pxg-frame-label]');
  var liveBox = wrap.querySelector('.pxg-live');

  var $w = document.getElementById('px-width');
  var $h = document.getElementById('px-height');
  var $ral = document.getElementById('px-ral-code');
  var objectUrl = null;
  var objectKey = null;

  var IMG_RE = /\.(jpe?g|png|gif|webp|bmp|tiff?|svg|avif)$/i;
  function isImageFile(f) {
    if (!f) return false;
    if (f.type && f.type.indexOf('image/') === 0) return true;
    return IMG_RE.test(f.name || '');
  }

  function firstUploadedImage() {
    if (!pxRoot) return null;
    var inputs = pxRoot.querySelectorAll('.px-upinput');
    for (var i = 0; i < inputs.length; i++) {
      var f = inputs[i].files && inputs[i].files[0];
      if (f && isImageFile(f)) return f;
    }
    return null;
  }

  function selectedCatalogImg() {
    if (!pxRoot) return null;
    var toggle = document.getElementById('px-design-toggle');
    if (!toggle || !toggle.checked) return null;
    var panel = pxRoot.querySelector('.px-design-panel.px-active-panel') ||
                pxRoot.querySelector('.px-design-panel');
    if (!panel) return null;
    var r = panel.querySelector('input[type="radio"]:checked');
    if (!r) return null;
    var tile = r.closest('.px-dtile');
    var img = tile ? tile.querySelector('.px-dimg') : null;
    if (!img) return null;
    return img.dataset.full || img.currentSrc || img.src || null;
  }

  // Frame colour: the radios carry data-name / data-ral but no hex, so we
  // read the rendered swatch's background colour next to the checked radio.
  function transparent(bg) {
    return !bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)';
  }
  function selectedColour() {
    if (!pxRoot) return { css: '#c9c9c9', isRal: false };
    var r = pxRoot.querySelector('input[name="px-fcolor"]:checked');
    if (!r) return { css: '#c9c9c9', isRal: false };
    if (r.dataset.ral === '1') return { css: '#fbbf1e', isRal: true };
    var scope = r.closest('label') || r.parentElement;
    if (scope) {
      var els = [scope].concat(Array.prototype.slice.call(scope.querySelectorAll('*')));
      for (var i = 0; i < els.length; i++) {
        if (els[i] === r) continue;
        var bg = window.getComputedStyle(els[i]).backgroundColor;
        if (!transparent(bg)) return { css: bg, isRal: false };
      }
    }
    return { css: '#c9c9c9', isRal: false };
  }

  function renderPreview() {
    if (!frame || !liveBox) return;
    var panel = wrap.querySelector('[data-pxg-panel="config"]');
    if (!panel || panel.hasAttribute('hidden')) return;

    var w = $w ? parseInt($w.value, 10) : NaN;
    var h = $h ? parseInt($h.value, 10) : NaN;
    if (!w || w < 1) w = 300;
    if (!h || h < 1) h = 150;

    var vw = liveBox.clientWidth || 640;
    var vh = liveBox.clientHeight || 440;
    var scale = Math.min((vw * 0.82) / w, (vh * 0.78) / h);
    frame.style.width = Math.round(w * scale) + 'px';
    frame.style.height = Math.round(h * scale) + 'px';

    var col = selectedColour();
    frame.style.border = '5px ' + (col.isRal ? 'dashed' : 'solid') + ' ' + col.css;

    if (frameLabel) {
      var ralTxt = (col.isRal && $ral && $ral.value.trim()) ? ' \u00b7 ' + $ral.value.trim() : '';
      frameLabel.textContent = w + ' \u00d7 ' + h + ' cm' + ralTxt;
    }

    // motif: uploaded file wins, then catalogue motif, then neutral placeholder
    var file = firstUploadedImage();
    if (file) {
      var key = file.name + ':' + file.size + ':' + file.lastModified;
      if (key !== objectKey) {
        if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch (e) {} }
        objectUrl = URL.createObjectURL(file);
        objectKey = key;
      }
      frameImg.src = objectUrl;
      frameImg.hidden = false;
      frame.style.backgroundImage = 'none';
      return;
    }
    if (objectUrl) {
      try { URL.revokeObjectURL(objectUrl); } catch (e) {}
      objectUrl = null; objectKey = null;
    }
    var catSrc = selectedCatalogImg();
    if (catSrc) {
      frameImg.src = catSrc;
      frameImg.hidden = false;
      frame.style.backgroundImage = 'none';
      return;
    }
    frameImg.hidden = true;
    frameImg.removeAttribute('src');
    frame.style.backgroundImage = '';
  }

  if (pxRoot && hasFlatFreeDims) {
    // instant feedback while typing / clicking
    ['input', 'change'].forEach(function (ev) {
      pxRoot.addEventListener(ev, function (e) {
        var t = e.target;
        if (!t) return;
        if (t.id === 'px-width' || t.id === 'px-height' || t.id === 'px-ral-code' ||
            t.name === 'px-fcolor' || t.classList.contains('px-upinput') ||
            t.id === 'px-design-toggle' ||
            (t.type === 'radio' && t.closest('.px-design-panel'))) {
          // switch to the config tab when a motif was just chosen
          if (t.classList.contains('px-upinput') ||
              (t.type === 'radio' && t.closest('.px-design-panel'))) {
            activateTab('config');
          }
          renderPreview();
        }
      });
    });

    // universal re-sync: every compute() rewrites the price node — covers the
    // stepper buttons (which set input.value without firing events), file
    // removal via the ✕ buttons, execution/print toggles, everything.
    var priceNet = document.getElementById('px-price-net');
    if (priceNet && typeof MutationObserver === 'function') {
      new MutationObserver(function () { renderPreview(); })
        .observe(priceNet, { childList: true, characterData: true, subtree: true });
    }
  }

  var resizeT;
  window.addEventListener('resize', function () {
    clearTimeout(resizeT);
    resizeT = setTimeout(renderPreview, 120);
  });

  renderPreview();
})();